import * as React from 'react';
import { Search } from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Separator } from '../ui/separator';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PrimaryAction {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  subtitle: string;
}

export interface PickerPopoverButtonProps<T> {
  emptyText: string;
  getItemKey: (item: T) => string;
  items: T[];
  listHeader: string;
  onItemClick: (item: T) => void;
  onOpenChange: (open: boolean) => void;
  onSearchChange: (value: string) => void;
  open: boolean;
  primaryAction?: PrimaryAction;
  renderItem: (item: T) => React.ReactNode;
  searchLabel: string;
  searchValue: string;
  triggerIcon: React.ReactNode;
  triggerLabel: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function PickerPopoverButton<T>({
  emptyText,
  getItemKey,
  items,
  listHeader,
  onItemClick,
  onOpenChange,
  onSearchChange,
  open,
  primaryAction,
  renderItem,
  searchLabel,
  searchValue,
  triggerIcon,
  triggerLabel,
}: PickerPopoverButtonProps<T>): React.JSX.Element {
  return (
    <Popover onOpenChange={onOpenChange} open={open}>
      <PopoverTrigger asChild>
        <Button
          className="h-9 gap-1.5 rounded-md px-3 text-sm shadow-sm hover:shadow-md"
          type="button"
          variant="default"
        >
          {triggerIcon}
          {triggerLabel}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-2">
        <div className="relative mb-2">
          <Search className="absolute start-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            aria-label={searchLabel}
            className="h-8 ps-8 text-sm border-0 bg-muted/50 focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-2"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onSearchChange(e.target.value)}
            placeholder={searchLabel}
            value={searchValue}
          />
        </div>
        {primaryAction && (
          <>
            <button
              className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-left hover:bg-muted transition-colors"
              onClick={primaryAction.onClick}
              type="button"
            >
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10">
                {primaryAction.icon}
              </div>
              <div className="min-w-0">
                <p className="font-medium truncate">{primaryAction.label}</p>
                <p className="text-[11px] text-muted-foreground">{primaryAction.subtitle}</p>
              </div>
            </button>
            <Separator className="my-2" />
          </>
        )}
        <p className="mb-1 px-3 text-xs font-semibold text-muted-foreground">{listHeader}</p>
        {items.length === 0 ? (
          <p className="px-3 py-2 text-sm text-muted-foreground">{emptyText}</p>
        ) : (
          <div className="max-h-48 overflow-y-auto">
            {items.map((item) => (
              <button
                key={getItemKey(item)}
                className="flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-sm text-left hover:bg-muted transition-colors"
                onClick={() => onItemClick(item)}
                type="button"
              >
                {renderItem(item)}
              </button>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
